import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminNav } from '../components/AdminNav';
import { Modal } from '../components/Modal';
import { useAdminApi, AdminApiError } from '../api/useAdminApi';

interface ReleaseResp {
  released: boolean;
  eventId: string;
  eventSubject: string;
  originalEnd: string;
  newEnd: string;
}

interface NextSteps {
  title: string;
  why: string;
  powershell: string;
}

interface UpsertResp {
  room: RoomDoc;
  nextSteps?: NextSteps;
}

interface RoomDoc {
  id: string;
  roomId: string;
  displayName: string;
  mailbox: string;
  timeZone: string;
  capacity?: number;
  floor?: string;
  active: boolean;
}

interface RoomFormState {
  roomId: string;
  displayName: string;
  mailbox: string;
  timeZone: string;
  capacity: string;          // string for form input, parsed on save
  floor: string;
  active: boolean;
  isNew: boolean;
}

const EMPTY: RoomFormState = {
  roomId: '', displayName: '', mailbox: '', timeZone: 'America/Denver',
  capacity: '', floor: '', active: true, isNew: true,
};

export function AdminRooms() {
  const { apiFetch, signedIn } = useAdminApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RoomFormState | null>(null);
  const [releasing, setReleasing] = useState<RoomDoc | null>(null);
  const [releaseConfirm, setReleaseConfirm] = useState('');
  const [releaseSuccess, setReleaseSuccess] = useState<ReleaseResp | null>(null);
  const [pendingNextSteps, setPendingNextSteps] = useState<NextSteps | null>(null);

  const releaseRoom = useMutation({
    mutationFn: ({ roomId, confirmation }: { roomId: string; confirmation: string }) =>
      apiFetch<ReleaseResp>(`/api/manage/rooms/${encodeURIComponent(roomId)}/release`, {
        method: 'POST',
        body: JSON.stringify({ confirmation }),
      }),
    onSuccess: (resp) => {
      setReleasing(null);
      setReleaseConfirm('');
      setReleaseSuccess(resp);
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'rooms'],
    queryFn: () => apiFetch<{ rooms: RoomDoc[] }>('/api/manage/rooms'),
    enabled: signedIn,
  });

  const saveRoom = useMutation({
    mutationFn: async (form: RoomFormState) => {
      const payload = {
        roomId: form.roomId.trim().toLowerCase(),
        displayName: form.displayName.trim(),
        mailbox: form.mailbox.trim(),
        timeZone: form.timeZone.trim(),
        capacity: form.capacity ? Number(form.capacity) : undefined,
        floor: form.floor.trim() || undefined,
        active: form.active,
      };
      return apiFetch<UpsertResp>('/api/manage/rooms', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['admin', 'rooms'] });
      setEditing(null);
      if (resp.nextSteps) setPendingNextSteps(resp.nextSteps);
    },
  });

  function startEdit(room: RoomDoc) {
    setEditing({
      roomId: room.roomId,
      displayName: room.displayName,
      mailbox: room.mailbox,
      timeZone: room.timeZone,
      capacity: room.capacity?.toString() ?? '',
      floor: room.floor ?? '',
      active: room.active,
      isNew: false,
    });
  }

  if (!signedIn) {
    return (
      <>
        <AdminNav />
        <section className="card"><p className="muted">Sign in on the Overview page first.</p></section>
      </>
    );
  }

  return (
    <>
      <AdminNav />

      <header className="admin-page-header">
        <h1>Rooms</h1>
        <button onClick={() => setEditing({ ...EMPTY })}>+ New room</button>
      </header>

      <section className="card">
        {isLoading ? <p className="muted">Loading rooms…</p> : null}
        {error ? (
          <p className="error">{(error as AdminApiError).message}</p>
        ) : null}
        {data ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Display name</th>
                <th>Room ID</th>
                <th>Mailbox</th>
                <th>Time zone</th>
                <th>Seats</th>
                <th>Active</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.rooms.map((r) => (
                <tr key={r.roomId} className={!r.active ? 'is-inactive' : ''}>
                  <td><strong>{r.displayName}</strong></td>
                  <td><code>{r.roomId}</code></td>
                  <td>{r.mailbox}</td>
                  <td>{r.timeZone}</td>
                  <td>{r.capacity ?? '—'}</td>
                  <td>
                    <span className={`pill ${r.active ? 'pill-on' : 'pill-off'}`}>
                      {r.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button className="link-button" onClick={() => startEdit(r)}>Edit</button>
                    {r.active ? (
                      <>
                        {' · '}
                        <button
                          className="link-button link-button-destructive"
                          onClick={() => { setReleasing(r); setReleaseConfirm(''); releaseRoom.reset(); }}
                        >
                          Force-release
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
              {data.rooms.length === 0 ? (
                <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: '1rem' }}>
                  No rooms yet. Click "+ New room" to add one.
                </td></tr>
              ) : null}
            </tbody>
          </table>
        ) : null}
      </section>

      {releasing ? (
        <Modal title={`Force-release ${releasing.displayName}?`} onClose={() => setReleasing(null)}>
          <p>
            This ends the in-progress meeting on <code>{releasing.mailbox}</code> RIGHT NOW —
            a Graph PATCH truncating the event's end to the current time. The organizer
            and any attendees see the meeting end immediately.
          </p>
          <p className="error">
            <strong>This is auditable and not undoable.</strong> Every force-release is logged
            to App Insights with your account.
          </p>
          <label>
            <span>Type <code>{releasing.roomId}</code> to confirm</span>
            <input
              autoFocus
              value={releaseConfirm}
              onChange={(e) => setReleaseConfirm(e.target.value)}
              placeholder={releasing.roomId}
            />
          </label>

          {releaseRoom.error ? (
            <p className="error" style={{ marginTop: '0.5rem' }}>
              {(releaseRoom.error as AdminApiError).message}
            </p>
          ) : null}

          <div className="admin-form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="button-ghost" onClick={() => setReleasing(null)}>Cancel</button>
            <button
              type="button"
              className="button-destructive"
              disabled={releaseConfirm !== releasing.roomId || releaseRoom.isPending}
              onClick={() => releaseRoom.mutate({ roomId: releasing.roomId, confirmation: releaseConfirm })}
            >
              {releaseRoom.isPending ? 'Releasing…' : 'Force-release now'}
            </button>
          </div>
        </Modal>
      ) : null}

      {pendingNextSteps ? (
        <Modal title={pendingNextSteps.title} onClose={() => setPendingNextSteps(null)} wide>
          <p>{pendingNextSteps.why}</p>
          <CopyableCommand code={pendingNextSteps.powershell} />
          <p className="muted" style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
            Why isn't this automated? Doing it from the server would require granting the Graph
            credential <code>GroupMember.ReadWrite.All</code> — a tenant-wide group-write
            permission that lets the credential modify <em>any</em> group in the tenant. The
            convenience isn't worth the blast-radius expansion.
          </p>
          <div className="admin-form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" onClick={() => setPendingNextSteps(null)}>Done</button>
          </div>
        </Modal>
      ) : null}

      {releaseSuccess ? (
        <Modal title="Meeting released" onClose={() => setReleaseSuccess(null)}>
          <p>The in-progress meeting on this room has been ended.</p>
          <ul className="kv" style={{ marginTop: '0.5rem' }}>
            <li><span>subject</span><code>{releaseSuccess.eventSubject || '(no subject)'}</code></li>
            <li><span>was ending</span><code>{releaseSuccess.originalEnd}</code></li>
            <li><span>now ends</span><code>{releaseSuccess.newEnd}</code></li>
          </ul>
          <div className="admin-form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" onClick={() => setReleaseSuccess(null)}>Done</button>
          </div>
        </Modal>
      ) : null}

      {/* CopyableCommand defined below */}

      {editing ? (
        <Modal title={editing.isNew ? 'New room' : `Edit ${editing.displayName}`} onClose={() => setEditing(null)}>
          <form
            onSubmit={(e) => { e.preventDefault(); saveRoom.mutate(editing); }}
            className="admin-form"
          >
            <label>
              <span>Room ID</span>
              <input
                value={editing.roomId}
                onChange={(e) => setEditing({ ...editing, roomId: e.target.value })}
                disabled={!editing.isNew}
                placeholder="wolfcreek"
                required
              />
              <small className="muted">Lowercase, [a-z0-9-]. Becomes the URL path /kiosk/&lt;id&gt;.</small>
            </label>
            <label>
              <span>Display name</span>
              <input
                value={editing.displayName}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                placeholder="Wolf Creek"
                required
              />
            </label>
            <label>
              <span>Mailbox (SMTP)</span>
              <input
                type="email"
                value={editing.mailbox}
                onChange={(e) => setEditing({ ...editing, mailbox: e.target.value })}
                placeholder="WolfCreek@westerncon.com"
                required
              />
            </label>
            <div className="admin-form-row">
              <label>
                <span>Time zone</span>
                <input
                  value={editing.timeZone}
                  onChange={(e) => setEditing({ ...editing, timeZone: e.target.value })}
                  placeholder="America/Denver"
                  required
                />
              </label>
              <label>
                <span>Seats</span>
                <input
                  type="number" min={1} max={500}
                  value={editing.capacity}
                  onChange={(e) => setEditing({ ...editing, capacity: e.target.value })}
                  placeholder="(optional)"
                />
              </label>
            </div>
            <label>
              <span>Floor</span>
              <input
                value={editing.floor}
                onChange={(e) => setEditing({ ...editing, floor: e.target.value })}
                placeholder="(optional)"
              />
            </label>
            <label className="admin-form-checkbox">
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              />
              <span>Active (shows up on kiosks and admin lists)</span>
            </label>

            {saveRoom.error ? (
              <p className="error">{(saveRoom.error as AdminApiError).message}</p>
            ) : null}

            <div className="admin-form-actions">
              <button type="button" className="button-ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" disabled={saveRoom.isPending}>
                {saveRoom.isPending ? 'Saving…' : (editing.isNew ? 'Create room' : 'Save changes')}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </>
  );
}

function CopyableCommand({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); }
    catch { /* ignore — fallback select-all is below */ }
  };
  return (
    <div className="copyable-url">
      <textarea readOnly value={code} onFocus={(e) => e.currentTarget.select()} rows={Math.max(3, code.split('\n').length + 1)} />
      <button type="button" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
    </div>
  );
}
